import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import {
  Album,
  Companion,
  FavoriteAlbumData,
  FeatureItem,
  Playlist,
} from 'src/types';
import {
  getPreferredLocale,
  setPreferredLocale as savePreferredLocale,
} from '../../utils/localeUtils';

export interface RecentTrack {
  track: {
    name: string;
    url: string; // This will be the resolved URL for the preferred locale
    trackIndex: number;
  };
  contributor: {
    id: string;
    givenName: string;
    thumbnailUrl?: string;
  };
  albumArtUrl: string;
  timestamp: number;
}

export interface musicStoreState {
  // Paginated data for Music/Serenity/Audiobook pages
  musicforyouList: Album[];
  serenityPlaylist: Album[];
  wisdomPlaylist: Album[];
  // Featured data for Home page (separate from paginated data)
  featuredMusic: Album[];
  featuredSerenity: Album[];
  featuredBooks: Album[];
  companionsList: Companion[];
  activePlaylist: null | Playlist;
  musicPlaying: boolean;
  recentTracks: RecentTrack[];
  activeWisdomData: {
    album: Album;
    albumIndex: number;
    itemIndex: number;
  } | null;
  featuredItems: FeatureItem[];
  shuffleEnabled: boolean;
  shuffleOrder: number[];
  repeatMode: 'off' | 'all' | 'one';
  volume: number;
  preMuteVolume: number; // Stores the volume before muting, for restoring on unmute
  preferredLocale: string;
  favouriteAlbums: FavoriteAlbumData[];
  favouriteContributors: any[];
  favouriteTracks: any[];
}

const initialState: musicStoreState = {
  // Paginated data
  musicforyouList: [],
  serenityPlaylist: [],
  wisdomPlaylist: [],
  // Featured data for home page
  featuredMusic: [],
  featuredSerenity: [],
  featuredBooks: [],
  companionsList: [],
  activePlaylist: null,
  musicPlaying: false,
  recentTracks: [],
  activeWisdomData: null,
  featuredItems: [],
  shuffleEnabled: false,
  shuffleOrder: [],
  repeatMode: 'off',
  volume: 1,
  preMuteVolume: 1, // Default to full volume
  preferredLocale: getPreferredLocale(),
  favouriteAlbums: [],
  favouriteContributors: [],
  favouriteTracks: [],
};

const musicStore = createSlice({
  name: 'music',
  initialState,
  reducers: {
    setActivePlaylist: (state, action: PayloadAction<Playlist | null>) => {
      // Check if this is a different album/playlist being loaded
      const currentAlbumId = state.activePlaylist?.album?.id;
      const newAlbumId = action.payload?.album?.id;
      const isDifferentPlaylist = currentAlbumId !== newAlbumId;

      state.activePlaylist = action.payload;

      // Reset shuffle and repeat settings when loading a different playlist
      if (isDifferentPlaylist && action.payload !== null) {
        state.shuffleEnabled = false;
        state.shuffleOrder = [];
        state.repeatMode = 'off';
      }
    },
    setMusicPlaying: (state, action: PayloadAction<boolean>) => {
      state.musicPlaying = action.payload;
    },
    setMusicForYouList: (state, action: PayloadAction<Album[]>) => {
      state.musicforyouList = action.payload;
    },
    appendMusicForYouList: (state, action: PayloadAction<Album[]>) => {
      state.musicforyouList = [...state.musicforyouList, ...action.payload];
    },
    setSerenityPlaylist: (state, action: PayloadAction<Album[]>) => {
      state.serenityPlaylist = action.payload;
    },
    appendSerenityPlaylist: (state, action: PayloadAction<Album[]>) => {
      state.serenityPlaylist = [...state.serenityPlaylist, ...action.payload];
    },
    setWisdomPlaylist: (state, action: PayloadAction<Album[]>) => {
      state.wisdomPlaylist = action.payload;
    },
    appendWisdomPlaylist: (state, action: PayloadAction<Album[]>) => {
      state.wisdomPlaylist = [...state.wisdomPlaylist, ...action.payload];
    },
    // Featured albums for home page (separate from paginated data)
    setFeaturedMusic: (state, action: PayloadAction<Album[]>) => {
      state.featuredMusic = action.payload;
    },
    setFeaturedSerenity: (state, action: PayloadAction<Album[]>) => {
      state.featuredSerenity = action.payload;
    },
    setFeaturedBooks: (state, action: PayloadAction<Album[]>) => {
      state.featuredBooks = action.payload;
    },
    setCompanionsList: (state, action: PayloadAction<Companion[]>) => {
      state.companionsList = action.payload;
    },
    addRecentTrack: (state, action: PayloadAction<RecentTrack>) => {
      // Remove the track if it already exists to avoid duplicates
      state.recentTracks = state.recentTracks.filter(
        (recentTrack) =>
          recentTrack.track.name !== action.payload.track.name ||
          recentTrack.contributor.id !== action.payload.contributor.id,
      );

      // Add the new track at the beginning
      state.recentTracks.unshift(action.payload);

      // Keep only the last 4 tracks
      if (state.recentTracks.length > 4) {
        state.recentTracks = state.recentTracks.slice(0, 4);
      }
    },
    setActiveWisdomData: (
      state,
      action: PayloadAction<{
        album: Album;
        albumIndex: number;
        itemIndex: number;
      }>,
    ) => {
      state.activeWisdomData = action.payload;
    },
    setFeaturedItems: (state, action: PayloadAction<FeatureItem[]>) => {
      state.featuredItems = action.payload;
    },
    setShuffleEnabled: (state, action: PayloadAction<boolean>) => {
      state.shuffleEnabled = action.payload;
      // Don't generate shuffle order here - let the hook handle it
      // This ensures proper shuffle order generation based on current track
      if (!action.payload) {
        state.shuffleOrder = [];
      }
    },
    setShuffleOrder: (state, action: PayloadAction<number[]>) => {
      state.shuffleOrder = action.payload;
    },
    setRepeatMode: (state, action: PayloadAction<'off' | 'all' | 'one'>) => {
      state.repeatMode = action.payload;
    },
    setVolume: (state, action: PayloadAction<number>) => {
      state.volume = Math.max(0, Math.min(1, action.payload)); // Clamp between 0 and 1
    },
    setPreMuteVolume: (state, action: PayloadAction<number>) => {
      state.preMuteVolume = Math.max(0, Math.min(1, action.payload)); // Clamp between 0 and 1
    },
    setPreferredLocale: (state, action: PayloadAction<string>) => {
      state.preferredLocale = action.payload;
      savePreferredLocale(action.payload);
    },
    setFavouriteAlbums: (state, action: PayloadAction<FavoriteAlbumData[]>) => {
      state.favouriteAlbums = action.payload;
    },
    setFavouriteContributors: (state, action: PayloadAction<any[]>) => {
      state.favouriteContributors = action.payload;
    },
    setFavouriteTracks: (state, action: PayloadAction<any[]>) => {
      state.favouriteTracks = action.payload;
    },
    addFavouriteAlbumToStore: (
      state,
      action: PayloadAction<FavoriteAlbumData>,
    ) => {
      // Check if album is already in favorites to avoid duplicates
      const exists = state.favouriteAlbums.some(
        (album) => album.id === action.payload.id,
      );
      if (!exists) {
        state.favouriteAlbums = [...state.favouriteAlbums, action.payload];
      }
    },
    addFavouriteTrackToStore: (state, action: PayloadAction<any>) => {
      // Check if track is already in favorites to avoid duplicates
      const exists = state.favouriteTracks.some(
        (track) => track.id === action.payload.id,
      );
      if (!exists) {
        state.favouriteTracks = [...state.favouriteTracks, action.payload];
      }
    },
    addFavouriteContributorToStore: (state, action: PayloadAction<any>) => {
      // Check if contributor is already in favorites to avoid duplicates
      const exists = state.favouriteContributors.some(
        (contributor) => contributor.id === action.payload.id,
      );
      if (!exists) {
        state.favouriteContributors = [
          ...state.favouriteContributors,
          action.payload,
        ];
      }
    },
    removeFavouriteAlbum: (state, action: PayloadAction<string>) => {
      state.favouriteAlbums = state.favouriteAlbums.filter(
        (album) => album.id !== action.payload,
      );
    },
    removeFavouriteTrack: (state, action: PayloadAction<string>) => {
      state.favouriteTracks = state.favouriteTracks.filter(
        (track) => track.id !== action.payload,
      );
    },
    removeFavouriteContributor: (state, action: PayloadAction<string>) => {
      state.favouriteContributors = state.favouriteContributors.filter(
        (contributor) => contributor.id !== action.payload,
      );
    },
    appendFavouriteAlbums: (
      state,
      action: PayloadAction<FavoriteAlbumData[]>,
    ) => {
      state.favouriteAlbums = [...state.favouriteAlbums, ...action.payload];
    },
    appendFavouriteTracks: (state, action: PayloadAction<any[]>) => {
      state.favouriteTracks = [...state.favouriteTracks, ...action.payload];
    },
    updateAlbumLikedStatus: (
      state,
      action: PayloadAction<{ albumId: string; isLiked: boolean }>,
    ) => {
      const { albumId, isLiked } = action.payload;
      console.log('🔄 Redux updateAlbumLikedStatus called:', {
        albumId,
        isLiked,
      });

      // Update in musicforyouList
      const musicIndex = state.musicforyouList.findIndex(
        (album) => album.id === albumId,
      );
      if (musicIndex !== -1) {
        const currentLikes = state.musicforyouList[musicIndex].likes || 0;
        const newLikes = isLiked
          ? currentLikes + 1
          : Math.max(0, currentLikes - 1);
        console.log('🎵 Updating musicforyouList album:', {
          currentLikes,
          newLikes,
          isLiked,
        });
        state.musicforyouList[musicIndex] = {
          ...state.musicforyouList[musicIndex],
          isLiked,
          likes: newLikes,
        };
      }

      // Update in serenityPlaylist
      const serenityIndex = state.serenityPlaylist.findIndex(
        (album) => album.id === albumId,
      );
      if (serenityIndex !== -1) {
        const currentLikes = state.serenityPlaylist[serenityIndex].likes || 0;
        const newLikes = isLiked
          ? currentLikes + 1
          : Math.max(0, currentLikes - 1);
        state.serenityPlaylist[serenityIndex] = {
          ...state.serenityPlaylist[serenityIndex],
          isLiked,
          likes: newLikes,
        };
      }

      // Update in wisdomPlaylist
      const wisdomIndex = state.wisdomPlaylist.findIndex(
        (album) => album.id === albumId,
      );
      if (wisdomIndex !== -1) {
        const currentLikes = state.wisdomPlaylist[wisdomIndex].likes || 0;
        const newLikes = isLiked
          ? currentLikes + 1
          : Math.max(0, currentLikes - 1);
        state.wisdomPlaylist[wisdomIndex] = {
          ...state.wisdomPlaylist[wisdomIndex],
          isLiked,
          likes: newLikes,
        };
      }

      // Update in featuredItems if it exists there
      const featuredIndex = state.featuredItems.findIndex(
        (item) => item.id === albumId,
      );
      if (featuredIndex !== -1) {
        // Note: featuredItems might not have isLiked property, but we can add it
        const currentLikes =
          (state.featuredItems[featuredIndex] as any).likes || 0;
        const newLikes = isLiked
          ? currentLikes + 1
          : Math.max(0, currentLikes - 1);
        state.featuredItems[featuredIndex] = {
          ...state.featuredItems[featuredIndex],
          isLiked,
          likes: newLikes,
        } as any;
      }

      // Update in featuredMusic if it exists there
      const featuredMusicIndex = state.featuredMusic.findIndex(
        (album) => album.id === albumId,
      );
      if (featuredMusicIndex !== -1) {
        const currentLikes = state.featuredMusic[featuredMusicIndex].likes || 0;
        const newLikes = isLiked
          ? currentLikes + 1
          : Math.max(0, currentLikes - 1);
        state.featuredMusic[featuredMusicIndex] = {
          ...state.featuredMusic[featuredMusicIndex],
          isLiked,
          likes: newLikes,
        };
      }

      // Update in featuredSerenity if it exists there
      const featuredSerenityIndex = state.featuredSerenity.findIndex(
        (album) => album.id === albumId,
      );
      if (featuredSerenityIndex !== -1) {
        const currentLikes =
          state.featuredSerenity[featuredSerenityIndex].likes || 0;
        const newLikes = isLiked
          ? currentLikes + 1
          : Math.max(0, currentLikes - 1);
        state.featuredSerenity[featuredSerenityIndex] = {
          ...state.featuredSerenity[featuredSerenityIndex],
          isLiked,
          likes: newLikes,
        };
      }

      // Update in featuredBooks if it exists there
      const featuredBooksIndex = state.featuredBooks.findIndex(
        (album) => album.id === albumId,
      );
      if (featuredBooksIndex !== -1) {
        const currentLikes = state.featuredBooks[featuredBooksIndex].likes || 0;
        const newLikes = isLiked
          ? currentLikes + 1
          : Math.max(0, currentLikes - 1);
        state.featuredBooks[featuredBooksIndex] = {
          ...state.featuredBooks[featuredBooksIndex],
          isLiked,
          likes: newLikes,
        };
      }

      // Update in activePlaylist if it matches the albumId
      if (state.activePlaylist && state.activePlaylist.album.id === albumId) {
        const currentLikes = state.activePlaylist.album.likes || 0;
        state.activePlaylist.album = {
          ...state.activePlaylist.album,
          isLiked,
          likes: isLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1),
        };
      }
    },
    updateTrackLikedStatus: (
      state,
      action: PayloadAction<{ trackId: string; isLiked: boolean }>,
    ) => {
      const { trackId, isLiked } = action.payload;

      // Update in activePlaylist if the current track matches
      if (state.activePlaylist && state.activePlaylist.album.tracks) {
        const trackIndex = state.activePlaylist.album.tracks.findIndex(
          (track) => track.id === trackId,
        );
        if (trackIndex !== -1) {
          state.activePlaylist.album.tracks[trackIndex] = {
            ...state.activePlaylist.album.tracks[trackIndex],
            isLiked,
          };
        }
      }

      // Update in all album collections
      const updateTracksInAlbums = (albums: any[]) => {
        albums.forEach((album, albumIndex) => {
          if (album.tracks) {
            const trackIndex = album.tracks.findIndex(
              (track: any) => track.id === trackId,
            );
            if (trackIndex !== -1) {
              albums[albumIndex].tracks[trackIndex] = {
                ...albums[albumIndex].tracks[trackIndex],
                isLiked,
              };
            }
          }
        });
      };

      updateTracksInAlbums(state.musicforyouList);
      updateTracksInAlbums(state.serenityPlaylist);
      updateTracksInAlbums(state.wisdomPlaylist);
      updateTracksInAlbums(state.featuredItems);
    },
    updateAlbumContributorMedia: (
      state,
      action: PayloadAction<{
        contributorId: string;
        mediaData: {
          thumbnailUrl?: string;
          videoDesktopUrl?: string;
          videoMobileUrl?: string;
        };
      }>,
    ) => {
      const { contributorId, mediaData } = action.payload;

      // Update in musicforyouList
      state.musicforyouList.forEach((album) => {
        album.contributors.forEach((contributor) => {
          if (contributor.id === contributorId) {
            if (mediaData.thumbnailUrl)
              contributor.thumbnailUrl = mediaData.thumbnailUrl;
            if (mediaData.videoDesktopUrl)
              contributor.videoDesktopUrl = mediaData.videoDesktopUrl;
            if (mediaData.videoMobileUrl)
              contributor.videoMobileUrl = mediaData.videoMobileUrl;
          }
        });
      });

      // Update in serenityPlaylist
      state.serenityPlaylist.forEach((album) => {
        album.contributors.forEach((contributor) => {
          if (contributor.id === contributorId) {
            if (mediaData.thumbnailUrl)
              contributor.thumbnailUrl = mediaData.thumbnailUrl;
            if (mediaData.videoDesktopUrl)
              contributor.videoDesktopUrl = mediaData.videoDesktopUrl;
            if (mediaData.videoMobileUrl)
              contributor.videoMobileUrl = mediaData.videoMobileUrl;
          }
        });
      });

      // Update in wisdomPlaylist
      state.wisdomPlaylist.forEach((album) => {
        album.contributors.forEach((contributor) => {
          if (contributor.id === contributorId) {
            if (mediaData.thumbnailUrl)
              contributor.thumbnailUrl = mediaData.thumbnailUrl;
            if (mediaData.videoDesktopUrl)
              contributor.videoDesktopUrl = mediaData.videoDesktopUrl;
            if (mediaData.videoMobileUrl)
              contributor.videoMobileUrl = mediaData.videoMobileUrl;
          }
        });
      });

      // Update in activePlaylist
      if (state.activePlaylist) {
        state.activePlaylist.album.contributors.forEach((contributor) => {
          if (contributor.id === contributorId) {
            if (mediaData.thumbnailUrl)
              contributor.thumbnailUrl = mediaData.thumbnailUrl;
            if (mediaData.videoDesktopUrl)
              contributor.videoDesktopUrl = mediaData.videoDesktopUrl;
            if (mediaData.videoMobileUrl)
              contributor.videoMobileUrl = mediaData.videoMobileUrl;
          }
        });
      }
    },
  },
});

export const {
  setActivePlaylist,
  setMusicPlaying,
  setMusicForYouList,
  appendMusicForYouList,
  setSerenityPlaylist,
  appendSerenityPlaylist,
  setWisdomPlaylist,
  appendWisdomPlaylist,
  setFeaturedMusic,
  setFeaturedSerenity,
  setFeaturedBooks,
  setCompanionsList,
  addRecentTrack,
  setActiveWisdomData,
  setFeaturedItems,
  setShuffleEnabled,
  setShuffleOrder,
  setRepeatMode,
  setVolume,
  setPreMuteVolume,
  setPreferredLocale,
  setFavouriteAlbums,
  setFavouriteContributors,
  setFavouriteTracks,
  addFavouriteAlbumToStore,
  addFavouriteTrackToStore,
  addFavouriteContributorToStore,
  removeFavouriteAlbum,
  removeFavouriteTrack,
  removeFavouriteContributor,
  appendFavouriteAlbums,
  appendFavouriteTracks,
  updateAlbumLikedStatus,
  updateTrackLikedStatus,
  updateAlbumContributorMedia,
} = musicStore.actions;
export const getMusicReducer = (state: RootState) => state.musicReducer;
export default musicStore.reducer;
